import React, { useState } from 'react';
import { X, FileText, Download, Loader, CheckCircle } from 'lucide-react';
import { useScan } from '../contexts/ScanContext';

const ReportGeneratorModal = ({ scanId, onClose }) => {
  const { generateReport } = useScan();
  const [format, setFormat] = useState('pdf');
  const [reportType, setReportType] = useState('technical');
  const [includeCharts, setIncludeCharts] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleGenerateReport = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const options = {
        format,
        reportType,
        includeCharts,
      };

      const result = await generateReport(scanId, options);

      if (result) {
        // Normalize format to lowercase so MIME lookup is case-insensitive
        const actualFormat = (result.format || format).toLowerCase();
        const mimeTypes = { pdf: 'application/pdf', html: 'text/html', markdown: 'text/markdown' };

        if (result.downloadUrl) {
          // Server saved a file artifact — open URL directly
          window.open(result.downloadUrl, '_blank');
          setSuccess(true);
          setTimeout(() => { onClose(); }, 2000);
        } else if (result.data) {
          // Inline content — create blob and trigger download
          const blob = new Blob([result.data], { type: mimeTypes[actualFormat] || 'text/plain' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `security-report-${scanId}-${Date.now()}.${actualFormat}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          setSuccess(true);
          setTimeout(() => { onClose(); }, 2000);
        } else {
          setError('Report generation succeeded but no file was returned. Please try again.');
        }
      }
    } catch (err) {
      console.error('Error generating report:', err);
      setError(err.message || 'Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full border border-gray-200">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-600 rounded-lg">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Generate Security Report</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 space-y-6">
          {/* Format Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Report Format
            </label>
            <div className="space-y-2">
              {[
                { value: 'pdf', label: 'PDF', description: 'Portable document format' },
                { value: 'html', label: 'HTML', description: 'Web page format' },
                { value: 'markdown', label: 'Markdown', description: 'Plain text with formatting' }
              ].map((option) => (
                <label
                  key={option.value}
                  className={`flex items-start p-3 rounded-lg border cursor-pointer transition-all ${
                    format === option.value
                      ? 'bg-teal-50 border-teal-400'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <input
                    type="radio"
                    name="format"
                    value={option.value}
                    checked={format === option.value}
                    onChange={(e) => setFormat(e.target.value)}
                    className="mt-1 mr-3"
                    disabled={loading}
                  />
                  <div>
                    <div className="text-gray-900 font-medium">{option.label}</div>
                    <div className="text-gray-500 text-xs">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Report Type Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Report Type
            </label>
            <div className="space-y-2">
              {[
                { value: 'technical', label: 'Technical Report', description: 'Detailed technical analysis for developers' },
                { value: 'executive', label: 'Executive Summary', description: 'High-level overview for stakeholders' }
              ].map((option) => (
                <label
                  key={option.value}
                  className={`flex items-start p-3 rounded-lg border cursor-pointer transition-all ${
                    reportType === option.value
                      ? 'bg-teal-50 border-teal-400'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <input
                    type="radio"
                    name="reportType"
                    value={option.value}
                    checked={reportType === option.value}
                    onChange={(e) => setReportType(e.target.value)}
                    className="mt-1 mr-3"
                    disabled={loading}
                  />
                  <div>
                    <div className="text-gray-900 font-medium">{option.label}</div>
                    <div className="text-gray-500 text-xs">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Include Charts Toggle */}
          <div>
            <label className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200 cursor-pointer hover:bg-gray-100 transition-all">
              <div>
                <div className="text-gray-900 font-medium">Include Charts</div>
                <div className="text-gray-500 text-xs">Add visual charts and graphs to the report</div>
              </div>
              <input
                type="checkbox"
                checked={includeCharts}
                onChange={(e) => setIncludeCharts(e.target.checked)}
                className="w-5 h-5"
                disabled={loading}
              />
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-green-600 text-sm">Report generated successfully!</p>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleGenerateReport}
            disabled={loading || !scanId}
            className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Generate Report
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportGeneratorModal;
